import React, { PropsWithChildren, useMemo } from "react";
import {
  LayoutChangeEvent,
  StyleProp,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewProps,
  ViewStyle,
} from "react-native";
import { BoxProps } from "./BoxProps";
import { useBoxStyle } from "./useBoxStyle";

export type TouchableBoxProps = PropsWithChildren<
  BoxProps & {
    style?: StyleProp<ViewStyle>;
    tabIndex?: number;
    ref?: React.RefObject<View | null>;
  } & Pick<
      TouchableOpacityProps,
      | "onPress"
      | "onPressIn"
      | "onPressOut"
      | "disabled"
      | "hitSlop"
      | "pressRetentionOffset"
      | "activeOpacity"
    >
>;

export const TouchableBox = ({
  onPress,
  onPressIn,
  onPressOut,
  children,
  style,
  disabled,
  hitSlop,
  pressRetentionOffset,
  activeOpacity,
  ref,
  ...props
}: TouchableBoxProps) => {
  const boxStyle = useBoxStyle(props);

  return (
    <TouchableOpacity
      ref={ref}
      testID={props.testID}
      pressRetentionOffset={pressRetentionOffset}
      onPress={onPress}
      hitSlop={hitSlop}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={disabled}
      activeOpacity={activeOpacity}
      style={[boxStyle, style]}
      {...{ tabIndex: props.tabIndex }}
    >
      {children}
    </TouchableOpacity>
  );
};

type _BoxProps = PropsWithChildren<
  BoxProps & {
    style?: StyleProp<ViewStyle>;
    pointerEvents?: ViewProps["pointerEvents"];
    ref?: React.RefObject<View | null>;
    onLayout?: (event: LayoutChangeEvent) => void;
  }
>;

export const Box = ({
  children,
  style,
  onLayout,
  pointerEvents,
  ref,
  ...props
}: _BoxProps) => {
  const boxStyle = useBoxStyle(props);
  const s = useMemo(() => [boxStyle, style], [boxStyle, style]);
  return (
    <View
      pointerEvents={pointerEvents}
      ref={ref}
      testID={props.testID}
      onLayout={onLayout}
      style={s}
    >
      {children}
    </View>
  );
};
